import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { useFiscalDeLaEmpresa } from '../sesion/fiscal';
import { FichaDeSeguimiento, type Etiqueta } from '../componentes/FichaDeSeguimiento';

interface Cliente {
  readonly id: string;
  readonly tipoDeIdentificacion: string;
  readonly identificacion: string | null;
  readonly razonSocial: string;
  readonly nombreComercial: string | null;
  readonly condicionIva: string;
  readonly telefono: string | null;
  readonly correo: string | null;
  readonly activo: boolean;
  readonly esCliente: boolean;
  readonly esProveedor: boolean;
  readonly limiteDeCredito: string | null;
  readonly diasDePagoDelCliente: number | null;
}

/**
 * Los importes llegan con cuatro decimales porque así se guardan. En pantalla sobran dos: un
 * límite de crédito de "500000.0000" se lee peor que "500000.00" y no dice nada más.
 */
function sinCerosDeMas(importe: string): string {
  return /\.\d{2}00$/.test(importe) ? importe.slice(0, -2) : importe;
}

/**
 * Clientes: a quién le vende el comercio.
 *
 * Es la misma tabla de terceros que la pantalla de proveedores, filtrada por el otro perfil. Un
 * tercero puede estar en las dos, y cuando lo está la columna lo dice: es el corralón al que se
 * le compra cemento y se le vende pintura, y verlo en pantalla es lo que evita que alguien lo
 * cargue dos veces.
 *
 * El límite de crédito se muestra desde acá aunque todavía no haya venta en cuenta corriente: es
 * el número que en el incremento que viene decide si una venta fiada se puede cerrar, y conviene
 * que el comercio lo tenga cargado antes de necesitarlo.
 */
export function Clientes(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeCrear = usePermiso('TERCEROS.TERCERO.CREAR');
  const puedeEditarElPerfil = usePermiso('TERCEROS.CLIENTE.EDITAR');

  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fiscal = useFiscalDeLaEmpresa();
  const [tipoDeIdentificacion, setTipo] = useState(fiscal.tiposDeTercero[0] ?? 'SIN_IDENTIFICAR');
  const [identificacion, setIdentificacion] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [nombreComercial, setNombreComercial] = useState('');
  const [condicionIva, setCondicion] = useState<string>(
    fiscal.paquete?.condicionDelReceptorPorOmision ?? 'consumidor_final',
  );
  const [limiteDeCredito, setLimite] = useState('0');
  const [diasDePago, setDiasDePago] = useState('0');
  const [telefono, setTelefono] = useState('');
  const [correo, setCorreo] = useState('');

  const [etiquetaFiltrada, setEtiquetaFiltrada] = useState('');

  const etiquetas = useQuery({
    queryKey: ['etiquetas'],
    queryFn: () => pedir<Etiqueta[]>('/etiquetas'),
  });

  const consulta = useQuery({
    queryKey: ['clientes', busqueda, etiquetaFiltrada],
    queryFn: () => {
      // El filtro lo resuelve el servidor: filtrar acá filtraría los doscientos que llegaron, no
      // los que hay.
      const parametros = new URLSearchParams({ soloClientes: 'true' });
      if (busqueda.trim() !== '') parametros.set('texto', busqueda.trim());
      if (etiquetaFiltrada !== '') parametros.set('etiquetaId', etiquetaFiltrada);
      return pedir<Cliente[]>(`/terceros?${parametros.toString()}`);
    },
  });

  const crear = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/terceros', {
        method: 'POST',
        cuerpo: {
          tipoDeIdentificacion,
          ...(identificacion.trim() === '' ? {} : { identificacion: identificacion.trim() }),
          razonSocial: razonSocial.trim(),
          ...(nombreComercial.trim() === '' ? {} : { nombreComercial: nombreComercial.trim() }),
          condicionIva,
          ...(telefono.trim() === '' ? {} : { telefono: telefono.trim() }),
          ...(correo.trim() === '' ? {} : { correo: correo.trim() }),
          cliente: {
            // Si no se puede fijar condiciones, el cliente nace sin crédito y sin plazo: es el
            // valor por defecto del servidor y acá no se lo contradice.
            ...(puedeEditarElPerfil
              ? { limiteDeCredito: limiteDeCredito.trim() === '' ? '0' : limiteDeCredito.trim() }
              : {}),
            diasDePago: Number(diasDePago) || 0,
          },
        },
      }),
    onSuccess: async () => {
      setAbierto(false);
      setIdentificacion('');
      setRazonSocial('');
      setNombreComercial('');
      setTelefono('');
      setCorreo('');
      setLimite('0');
      setDiasDePago('0');
      setError(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['clientes'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  /** El cliente cuyo seguimiento se está mirando. */
  const [siguiendo, setSiguiendo] = useState<Cliente | null>(null);

  const columnas: Columna<Cliente>[] = [
    {
      clave: 'razonSocial',
      titulo: t('clientes.razonSocial'),
      contenido: (fila) => (
        <>
          {fila.razonSocial}
          {fila.nombreComercial === null ? null : (
            <span className="block text-xs text-texto-tenue">{fila.nombreComercial}</span>
          )}
        </>
      ),
    },
    {
      clave: 'identificacion',
      titulo: t('clientes.identificacion'),
      contenido: (fila) => (
        <span className="numero">
          {fila.identificacion ?? '—'}
          <span className="block text-xs text-texto-tenue">
            {fiscal.nombreDeTipo(fila.tipoDeIdentificacion)}
          </span>
        </span>
      ),
    },
    {
      clave: 'condicionIva',
      titulo: t('clientes.condicionIva'),
      contenido: (fila) => fiscal.nombreDeCondicion(fila.condicionIva),
    },
    {
      clave: 'limiteDeCredito',
      titulo: t('clientes.limiteDeCredito'),
      numerica: true,
      // Los tres estados se dicen con palabras y no con un número: sin límite, no se le fía, o
      // el tope. Mostrar "0" a secas obliga a adivinar cuál de los tres es.
      contenido: (fila) =>
        fila.limiteDeCredito === null ? (
          <span className="text-texto-tenue">{t('clientes.sinLimite')}</span>
        ) : Number(fila.limiteDeCredito) === 0 ? (
          <span className="text-texto-tenue">{t('clientes.noSeLeFia')}</span>
        ) : (
          <span className="numero">{sinCerosDeMas(fila.limiteDeCredito)}</span>
        ),
    },
    {
      clave: 'diasDePago',
      titulo: t('clientes.diasDePago'),
      numerica: true,
      contenido: (fila) => (
        <span className="numero">
          {fila.diasDePagoDelCliente === null || fila.diasDePagoDelCliente === 0
            ? t('clientes.contado')
            : fila.diasDePagoDelCliente}
        </span>
      ),
    },
    {
      clave: 'tambienProveedor',
      titulo: t('clientes.tambienProveedor'),
      contenido: (fila) =>
        fila.esProveedor ? <Insignia tono="neutro">{t('clientes.esProveedor')}</Insignia> : '—',
    },
    {
      clave: 'contacto',
      titulo: t('clientes.telefono'),
      contenido: (fila) => fila.telefono ?? fila.correo ?? '—',
    },
    {
      clave: 'acciones',
      titulo: '',
      // Corregir, no borrar: un tercero con movimientos no se puede borrar sin dejar una cuenta
      // corriente apuntando a la nada.
      contenido: (fila) => (
        <div className="flex justify-end gap-1">
          <Boton
            onClick={() => {
              setSiguiendo(fila);
            }}
          >
            {t('clientes.seguimiento')}
          </Boton>
          {/* Gestionar y no corregir: lleva a la ficha, donde además de editar están la cuenta,
              los comprobantes y qué se lleva. */}
          <Link to="/clientes/$id" params={{ id: fila.id }}>
            <Boton tono="principal">{t('ficha.gestionar')}</Boton>
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('clientes.titulo')}
        descripcion={t('clientes.descripcion')}
        acciones={
          puedeCrear ? (
            <Boton
              tono="principal"
              onClick={() => {
                setAbierto(true);
                setError(null);
              }}
            >
              {t('clientes.nuevoCliente')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="max-w-sm flex-1">
            <Campo
              etiqueta={t('clientes.buscar')}
              type="search"
              value={busqueda}
              onChange={(evento) => {
                setBusqueda(evento.target.value);
              }}
            />
          </div>
          {/* El selector no aparece hasta que hay etiquetas: un filtro con una sola opción que
              dice «todas» es una promesa vacía ocupando lugar. */}
          {(etiquetas.data ?? []).length === 0 ? null : (
            <Selector
              etiqueta={t('clientes.filtrarPorEtiqueta')}
              value={etiquetaFiltrada}
              onChange={(evento) => {
                setEtiquetaFiltrada(evento.target.value);
              }}
            >
              <option value="">{t('clientes.todasLasEtiquetas')}</option>
              {(etiquetas.data ?? []).map((etiqueta) => (
                <option key={etiqueta.id} value={etiqueta.id}>
                  {etiqueta.nombre}
                </option>
              ))}
            </Selector>
          )}
        </div>

        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={(fila) => fila.id}
            vacio={<Vacio descripcion={t('clientes.sinClientes')} />}
          />
        ) : null}
      </Tarjeta>

      <Dialogo
        abierto={abierto}
        titulo={t('clientes.nuevoCliente')}
        onCerrar={() => {
          setAbierto(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            disabled={razonSocial.trim() === ''}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('clientes.razonSocial')}
            obligatorio
            maxLength={200}
            value={razonSocial}
            onChange={(evento) => {
              setRazonSocial(evento.target.value);
            }}
          />
          <Campo
            etiqueta={t('clientes.nombreComercial')}
            maxLength={160}
            value={nombreComercial}
            ayuda={t('clientes.ayudaNombreComercial')}
            onChange={(evento) => {
              setNombreComercial(evento.target.value);
            }}
          />

          <div className="grid grid-cols-2 gap-3">
            <Selector
              etiqueta={t('clientes.tipoDeIdentificacion')}
              value={tipoDeIdentificacion}
              onChange={(evento) => {
                setTipo(evento.target.value);
              }}
            >
              {fiscal.tiposDeTercero.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {fiscal.nombreDeTipo(tipo)}
                </option>
              ))}
            </Selector>
            <Campo
              etiqueta={t('clientes.identificacion')}
              value={identificacion}
              maxLength={32}
              disabled={tipoDeIdentificacion === 'SIN_IDENTIFICAR'}
              ayuda={t('clientes.ayudaIdentificacion')}
              onChange={(evento) => {
                setIdentificacion(evento.target.value);
              }}
            />
          </div>

          <Selector
            etiqueta={t('clientes.condicionIva')}
            value={condicionIva}
            onChange={(evento) => {
              setCondicion(evento.target.value);
            }}
          >
            {fiscal.condicionesDeTercero.map((condicion) => (
              <option key={condicion} value={condicion}>
                {fiscal.nombreDeCondicion(condicion)}
              </option>
            ))}
          </Selector>

          <div className="grid grid-cols-3 gap-3">
            <Campo
              etiqueta={t('clientes.limiteDeCredito')}
              inputMode="decimal"
              value={limiteDeCredito}
              disabled={!puedeEditarElPerfil}
              ayuda={t('clientes.ayudaLimite')}
              onChange={(evento) => {
                setLimite(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('clientes.diasDePago')}
              inputMode="numeric"
              value={diasDePago}
              disabled={!puedeEditarElPerfil}
              ayuda={t('clientes.ayudaDiasDePago')}
              onChange={(evento) => {
                setDiasDePago(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('clientes.telefono')}
              maxLength={40}
              value={telefono}
              onChange={(evento) => {
                setTelefono(evento.target.value);
              }}
            />
          </div>

          <Campo
            etiqueta={t('clientes.correo')}
            type="email"
            maxLength={160}
            value={correo}
            onChange={(evento) => {
              setCorreo(evento.target.value);
            }}
          />

          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>
      {siguiendo === null ? null : (
        <FichaDeSeguimiento
          terceroId={siguiendo.id}
          nombre={siguiendo.razonSocial}
          abierto
          onCerrar={() => {
            setSiguiendo(null);
          }}
        />
      )}
    </>
  );
}
